import type { CustomLayerInterface, CustomRenderMethod, Map as MLMap, MapGeoJSONFeature, MapSourceDataEvent, VectorSourceSpecification } from 'maplibre-gl';
import maplibregl from 'maplibre-gl';
import type React from 'react';
import { useEffect, useRef } from 'react';
import SunCalc from 'suncalc';
import * as THREE from 'three';

import { useOptionalMapLibre } from './MapLibreContext';
import { useSunlight } from './SunlightContext';

interface Buildings3DProps {
  enabled: boolean;
  highlightAreas?: GeoJSON.FeatureCollection<GeoJSON.MultiPolygon | GeoJSON.Polygon>;
  highlightColor?: string;
  defaultColor?: string;
}

export const Buildings3D: React.FC<Buildings3DProps> = ({ enabled, highlightAreas, highlightColor = '#f0d54e', defaultColor = '#f5f5f5' }) => {
  const map = useOptionalMapLibre();
  const { sunDate } = useSunlight();

  const lightRef = useRef<THREE.DirectionalLight | null>(null);
  const originRef = useRef<{ lng: number; lat: number } | null>(null);
  const sunDateRef = useRef<Date>(sunDate);
  useEffect(() => { sunDateRef.current = sunDate; }, [sunDate]);

  interface ModelTransform { translateX: number; translateY: number; translateZ: number; rotateX: number; rotateY: number; rotateZ: number; scale: number }
  const modelTransformRef = useRef<ModelTransform | null>(null);
  const buildingsGroupRef = useRef<THREE.Group | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.Camera | null>(null);
  const unitsPerMeterRef = useRef<number>(1);
  const groundRef = useRef<THREE.Mesh | null>(null);
  const initializedRef = useRef<boolean>(false);

  // Ensure we apply the correct sun-based light direction consistently
  const applyDirectionalLightFromSun = (dateOverride?: Date) => {
    if (!map || !enabled) { return; }
    if (!lightRef.current || !modelTransformRef.current) { return; }
    const center = map.getCenter();
    const date = dateOverride ?? sunDateRef.current ?? sunDate;
    const pos = SunCalc.getPosition(date, center.lat, center.lng);
    const altitude = Math.max(0, pos.altitude);
    const bearingFromNorth = pos.azimuth + Math.PI;
    const horizontal = Math.cos(altitude);
    const dir = new THREE.Vector3(
      Math.sin(bearingFromNorth) * horizontal,
      Math.sin(altitude),
      -Math.cos(bearingFromNorth) * horizontal,
    );
    const distanceMeters = 800;
    const targetY = (modelTransformRef.current as { translateZ: number }).translateZ;
    const target = new THREE.Vector3(0, targetY, 0);
    const sunPos = target.clone().addScaledVector(dir, distanceMeters);
    lightRef.current.position.copy(sunPos);
    lightRef.current.target.position.copy(target);
    lightRef.current.updateMatrixWorld();
    lightRef.current.target.updateMatrixWorld();
    try {
      map.triggerRepaint();
    } catch {}
  };

  useEffect(() => {
    if (!map) { return; }


    const sourceId = 'buildings-source';
    const probeLayerId = 'buildings-probe';
    const customLayerId = 'buildings-3d-threejs';

    // Helpers to manage layers/sources
    const cleanupCustomOnly = () => {
      try {
        if (!map.getStyle()) { return; }
        if (map.getLayer(customLayerId)) { map.removeLayer(customLayerId); }
      } catch (error) {
        console.warn('Map cleanup error:', error);
      }
    };

    const cleanupAll = () => {
      try {
        if (!map.getStyle()) { return; }
        if (map.getLayer(customLayerId)) { map.removeLayer(customLayerId); }
        if (map.getLayer(probeLayerId)) { map.removeLayer(probeLayerId); }
        if (map.getSource(sourceId)) { map.removeSource(sourceId); }
      } catch (error) {
        console.warn('Full cleanup error:', error);
      }
    };

    // Ensure source and transparent probe layer exist so tiles can load
    const ensureSourceAndProbe = () => {
      try {
        // Check if style is actually loaded before trying to add sources/layers
        if (!map.getStyle()) {
          return false;
        }
        
        // Use a more explicit URL format that MapLibre expects
        const tilesUrl = `${window.location.origin}/tiles/buildings/{z}/{x}/{y}.pbf`;
        if (!map.getSource(sourceId)) {
          const source: VectorSourceSpecification = {
            type: 'vector',
            tiles: [tilesUrl],
            scheme: 'xyz',
            minzoom: 14,
            maxzoom: 19,
          };
          map.addSource(sourceId, source);
        }

        if (!map.getLayer(probeLayerId)) {
          map.addLayer({
            id: probeLayerId,
            type: 'fill',
            source: sourceId,
            'source-layer': 'buildings',
            minzoom: 14,
            paint: { 'fill-opacity': 0.01 }, // keep virtually invisible but trigger tile fetching
          });
        }
        return true;
      } catch {
        return false;
      }
    };

    if (!map.isStyleLoaded()) {
      const onStyleOnce = () => {
        ensureSourceAndProbe();
        map.off('styledata', onStyleOnce);
      };
      map.on('styledata', onStyleOnce);
    } else {
      ensureSourceAndProbe();
    }

    if (!enabled) {
      // Immediately remove the 3D buildings layer when disabled
      cleanupCustomOnly();
      const onSourceDataDebug = (e: MapSourceDataEvent) => {
        if (e.sourceId === sourceId && e.isSourceLoaded) {
          map.off('sourcedata', onSourceDataDebug as never);
        }
      };
      map.on('sourcedata', onSourceDataDebug as never);
      return () => {
        map.off('sourcedata', onSourceDataDebug as never);
        cleanupCustomOnly();
      };
    }

    // Prepare highlight geometry utils (in WGS84)
    type Ring = number[][]; // [lng, lat][]
    type Poly = { outer: Ring; holes: Ring[] };
    const processFeature = (f: GeoJSON.Feature<GeoJSON.MultiPolygon | GeoJSON.Polygon>): Poly[] => {
      if (!f?.geometry) { return []; }
      
      if (f.geometry.type === 'Polygon') {
        const coords = f.geometry.coordinates as number[][][];
        const [outer, ...holes] = coords;
        return Array.isArray(outer) ? [{ outer, holes: holes ?? [] }] : [];
      }
      
      if (f.geometry.type === 'MultiPolygon') {
        const mp = f.geometry.coordinates as number[][][][];
        return mp.map((poly) => {
          const [outer, ...holes] = poly as unknown as number[][][];
          return { outer, holes: holes ?? [] };
        }).filter((p) => Array.isArray(p.outer));
      }
      
      return [];
    };

    const areaPolys: Poly[] = (() => {
      try {
        const features = highlightAreas?.features ?? [];
        return features.flatMap(processFeature);
      } catch {
        return [];
      }
    })();

    const isPointInRing = (lng: number, lat: number, ring: Ring): boolean => {
      let inside = false;
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const xi = ring[i][0]; const yi = ring[i][1];
        const xj = ring[j][0]; const yj = ring[j][1];
        const denom = (yj - yi) + 1e-9;
        const xCross = (((xj - xi) * (lat - yi)) / denom) + xi;
        const intersect = ((yi > lat) !== (yj > lat)) && (lng < xCross);
        if (intersect) { inside = !inside; }
      }
      return inside;
    };

    const isPointInPoly = (lng: number, lat: number, poly: Poly): boolean => {
      if (!isPointInRing(lng, lat, poly.outer)) { return false; }
      for (const hole of poly.holes) { if (isPointInRing(lng, lat, hole)) { return false; } }
      return true;
    };

    const isPointInAnyArea = (lng: number, lat: number): boolean => {
      for (const p of areaPolys) { if (isPointInPoly(lng, lat, p)) { return true; } }
      return false;
    };

    const initializeBuildings = () => {
      try {
        // Verify the map is ready and has a style before initializing
        if (!map.getStyle()) {
          return;
        }

        // Ensure source and probe are present
        if (!map.getSource(sourceId)) {
          return;
        }

        const computeOriginAndTransform = () => {
          const center = map.getCenter();
          originRef.current = { lng: center.lng, lat: center.lat };
          const mc = maplibregl.MercatorCoordinate.fromLngLat([center.lng, center.lat], 0);
          unitsPerMeterRef.current = mc.meterInMercatorCoordinateUnits();
          modelTransformRef.current = { translateX: mc.x, translateY: mc.y, translateZ: mc.z, rotateX: Math.PI / 2, rotateY: 0, rotateZ: 0, scale: unitsPerMeterRef.current };
        };

        const rebuildBuildings = () => {
          if (!sceneRef.current || !modelTransformRef.current || !originRef.current) { return; }
          if (buildingsGroupRef.current) { sceneRef.current.remove(buildingsGroupRef.current); }
          const group = new THREE.Group();
          buildingsGroupRef.current = group;

          // Prefer source features from loaded tiles to avoid viewport bbox filtering
          const features = (map.querySourceFeatures(sourceId, { sourceLayer: 'buildings' }) as unknown as MapGeoJSONFeature[]) || [];
          const maxFeatures = 750;


          const toLocalMeters = (lng: number, lat: number) => {
            const m = maplibregl.MercatorCoordinate.fromLngLat([lng, lat], 0);
            const dxUnits = m.x - (modelTransformRef.current as ModelTransform).translateX;
            const dyUnits = m.y - (modelTransformRef.current as ModelTransform).translateY;
            return { x: dxUnits / unitsPerMeterRef.current, y: dyUnits / unitsPerMeterRef.current };
          };

          const calculateCentroid = (polygon: number[][]) => {
            let sumX = 0; let sumY = 0; let count = 0;
            for (const [lng, lat] of polygon) { const { x, y } = toLocalMeters(lng, lat); sumX += x; sumY += y; count++; }
            return count > 0 ? { x: sumX / count, y: sumY / count } : { x: 0, y: 0 };
          };

          const buildShapeFromPolygon = (coords: number[][][], centroid: { x: number; y: number }) => {
            if (coords.length === 0) { return null; }
            const [outer, ...holes] = coords;
            const outerPts = outer.map(([lng, lat]) => { const { x, y } = toLocalMeters(lng, lat); return new THREE.Vector2(x - centroid.x, -(y - centroid.y)); });
            if (!THREE.ShapeUtils.isClockWise(outerPts)) { outerPts.reverse(); }
            const shape = new THREE.Shape(outerPts);
            for (const hole of holes) {
              const holePts = hole.map(([lng, lat]) => { const { x, y } = toLocalMeters(lng, lat); return new THREE.Vector2(x - centroid.x, -(y - centroid.y)); });
              if (THREE.ShapeUtils.isClockWise(holePts)) { holePts.reverse(); }
              shape.holes.push(new THREE.Path(holePts));
            }
            return shape;
          };

          const selectedFeatures: MapGeoJSONFeature[] = [...features]
            .map((feat) => {
              let outer: number[][] | null = null;
              if (feat.geometry?.type === 'Polygon') { const poly = feat.geometry.coordinates as unknown as number[][][]; outer = poly[0] || null; } else if (feat.geometry?.type === 'MultiPolygon') { const mp = feat.geometry.coordinates as unknown as number[][][][]; const first = mp[0] as unknown as number[][][] | undefined; outer = first ? first[0] : null; }
              const c = outer ? calculateCentroid(outer) : { x: Number.POSITIVE_INFINITY, y: Number.POSITIVE_INFINITY };
              const d2 = (c.x * c.x) + (c.y * c.y);
              return { feat, d2 } as unknown as { feat: MapGeoJSONFeature; d2: number };
            })
            .sort((a, b) => a.d2 - b.d2)
            .slice(0, maxFeatures)
            .map(({ feat }) => feat);

          // Median-based fallback height for "flat" buildings
          const FLAT_HEIGHT_THRESHOLD_METERS = 0.1;
          const heightValues: number[] = [];
          for (const f of selectedFeatures) {
            const p = (f.properties || {}) as Record<string, unknown>;
            const h = Number(p.height ?? p.render_height);
            if (Number.isFinite(h) && h >= FLAT_HEIGHT_THRESHOLD_METERS) {
              heightValues.push(h);
            }
          }
          let medianHeightMeters = 12;
          if (heightValues.length > 0) {
            heightValues.sort((a, b) => a - b);
            const mid = Math.floor(heightValues.length / 2);
            medianHeightMeters = heightValues.length % 2 !== 0
              ? heightValues[mid]
              : (heightValues[mid - 1] + heightValues[mid]) / 2;
          }

          const defaultMat = new THREE.MeshStandardMaterial({ color: new THREE.Color(defaultColor), metalness: 0.05, roughness: 0.95, side: THREE.FrontSide });
          const highlightMat = new THREE.MeshStandardMaterial({ color: new THREE.Color(highlightColor), metalness: 0.05, roughness: 0.9, side: THREE.FrontSide });

          for (const f of selectedFeatures) {
            const props = (f.properties || {}) as Record<string, unknown>;
            const raw = Number(props.height ?? props.render_height);
            const heightMeters = Number.isFinite(raw)
              ? (raw < FLAT_HEIGHT_THRESHOLD_METERS ? medianHeightMeters : raw)
              : medianHeightMeters;

            if (f.geometry?.type === 'Polygon') {
              const poly = f.geometry.coordinates as number[][][];
              const [outer] = poly;
              const centroid = calculateCentroid(outer);
              // Compute lng/lat centroid for area test (simple average)
              let sumLng = 0; let sumLat = 0; let cnt = 0;
              for (const [lng, lat] of outer) { sumLng += lng; sumLat += lat; cnt++; }
              const cLng = cnt > 0 ? (sumLng / cnt) : 0; const cLat = cnt > 0 ? (sumLat / cnt) : 0;
              const shape = buildShapeFromPolygon(poly, centroid);
              if (!shape) { continue; }
              const geom = new THREE.ExtrudeGeometry(shape, { depth: heightMeters, bevelEnabled: false });
              geom.computeVertexNormals();
              const useHighlight = isPointInAnyArea(cLng, cLat);
              const mesh = new THREE.Mesh(geom, useHighlight ? highlightMat : defaultMat);
              mesh.castShadow = true; mesh.receiveShadow = true;
              mesh.position.x = centroid.x; mesh.position.z = centroid.y; mesh.rotation.x = -Math.PI / 2; mesh.position.y = (modelTransformRef.current as ModelTransform).translateZ;
              group.add(mesh);
            } else if (f.geometry?.type === 'MultiPolygon') {
              const mp = f.geometry.coordinates as number[][][][];
              for (const poly of mp) {
                const polyCoords = poly as unknown as number[][][];
                const [outer] = polyCoords;
                const centroid = calculateCentroid(outer);
                let sumLng = 0; let sumLat = 0; let cnt = 0;
                for (const [lng, lat] of outer) { sumLng += lng; sumLat += lat; cnt++; }
                const cLng = cnt > 0 ? (sumLng / cnt) : 0; const cLat = cnt > 0 ? (sumLat / cnt) : 0;
                const shape = buildShapeFromPolygon(polyCoords, centroid);
                if (!shape) { continue; }
                const geom = new THREE.ExtrudeGeometry(shape, { depth: heightMeters, bevelEnabled: false });
                geom.computeVertexNormals();
                const useHighlight = isPointInAnyArea(cLng, cLat);
                const mesh = new THREE.Mesh(geom, useHighlight ? highlightMat : defaultMat);
                mesh.castShadow = true; mesh.receiveShadow = true;
                mesh.position.x = centroid.x; mesh.position.z = centroid.y; mesh.rotation.x = -Math.PI / 2; mesh.position.y = (modelTransformRef.current as ModelTransform).translateZ;
                group.add(mesh);
              }
            }
          }

          sceneRef.current.add(group);
          try {
            map.triggerRepaint();
          } catch {}
        };

        const customLayer: CustomLayerInterface = {
          id: customLayerId,
          type: 'custom',
          renderingMode: '3d',
          onAdd: (_map: MLMap, gl: WebGLRenderingContext) => {
            cameraRef.current = new THREE.Camera();
            sceneRef.current = new THREE.Scene();
            computeOriginAndTransform();
            const light = new THREE.DirectionalLight(0xffffff, 1);
            light.castShadow = true;
            light.position.set(100, 100, 100);
            (light.shadow.camera as THREE.OrthographicCamera).near = 0.1;
            (light.shadow.camera as THREE.OrthographicCamera).far = 2000;
            (light.shadow.camera as THREE.OrthographicCamera).left = -500;
            (light.shadow.camera as THREE.OrthographicCamera).right = 500;
            (light.shadow.camera as THREE.OrthographicCamera).top = 500;
            (light.shadow.camera as THREE.OrthographicCamera).bottom = -500;
            light.shadow.mapSize.width = 4096; light.shadow.mapSize.height = 4096;
            sceneRef.current.add(light); sceneRef.current.add(light.target);
            lightRef.current = light;
            const groundGeom = new THREE.PlaneGeometry(1000, 1000);
            const groundMat = new THREE.ShadowMaterial({ opacity: 0.5 });
            const ground = new THREE.Mesh(groundGeom, groundMat);
            ground.rotation.x = -Math.PI / 2; ground.position.y = (modelTransformRef.current as ModelTransform).translateZ; ground.receiveShadow = true;
            sceneRef.current.add(ground); groundRef.current = ground;
            
            // Create WebGL renderer with context loss handling
            try {
              rendererRef.current = new THREE.WebGLRenderer({ 
                canvas: map.getCanvas(), 
                context: gl, 
                antialias: true,
                preserveDrawingBuffer: false,
                powerPreference: 'high-performance',
              });
              rendererRef.current.shadowMap.enabled = true;
              rendererRef.current.shadowMap.type = THREE.PCFSoftShadowMap;
              rendererRef.current.autoClear = false;
              
              // Handle WebGL context loss
              const canvas = map.getCanvas();
              const handleContextLost = (event: Event) => {
                event.preventDefault();
              };
              const handleContextRestored = () => {
                rebuildBuildings();
              };
              canvas.addEventListener('webglcontextlost', handleContextLost);
              canvas.addEventListener('webglcontextrestored', handleContextRestored);
            } catch {
              return;
            }
            // Ensure initial light direction matches current sun position before any map interaction
            applyDirectionalLightFromSun();
            rebuildBuildings();
          },
          render: ((gl: WebGLRenderingContext, matrix: number[]) => {
            if (!sceneRef.current || !cameraRef.current || !rendererRef.current || !modelTransformRef.current) { return; }
            
            // Check if the canvas is visible and has dimensions before rendering
            const canvas = map.getCanvas();
            if (!canvas || canvas.offsetParent === null || canvas.clientWidth === 0 || canvas.clientHeight === 0) {
              return; // Skip rendering if canvas is hidden or has no dimensions
            }
            
            const mt = modelTransformRef.current;
            const rotationX = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(1, 0, 0), mt.rotateX);
            const rotationY = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 1, 0), mt.rotateY);
            const rotationZ = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 0, 1), mt.rotateZ);
            const m = new THREE.Matrix4().fromArray(matrix);
            const l = new THREE.Matrix4().makeTranslation(mt.translateX, mt.translateY, mt.translateZ).scale(new THREE.Vector3(mt.scale, -mt.scale, mt.scale)).multiply(rotationX).multiply(rotationY).multiply(rotationZ);
            cameraRef.current.projectionMatrix = m.multiply(l);
            
            try {
              gl.clear(gl.DEPTH_BUFFER_BIT);
              rendererRef.current.resetState();
              rendererRef.current.render(sceneRef.current, cameraRef.current);
              map.triggerRepaint();
            } catch (error) {
              // Silently handle render errors to prevent console spam when context is lost
              console.error(error);
            }
          }) as CustomRenderMethod,
          onRemove: () => {
            try {
              // Properly dispose of THREE.js resources
              if (buildingsGroupRef.current && sceneRef.current) {
                sceneRef.current.remove(buildingsGroupRef.current);
                buildingsGroupRef.current.traverse((child) => {
                  if (child instanceof THREE.Mesh) {
                    child.geometry?.dispose();
                    if (Array.isArray(child.material)) {
                      child.material.forEach((mat) => mat?.dispose());
                    } else {
                      child.material?.dispose();
                    }
                  }
                });
              }
              
              if (lightRef.current) {
                lightRef.current.dispose?.();
              }
              
              if (groundRef.current) {
                groundRef.current.geometry?.dispose();
                if (groundRef.current.material && !Array.isArray(groundRef.current.material)) {
                  groundRef.current.material.dispose();
                }
              }
              
              if (rendererRef.current) {
                rendererRef.current.dispose();
                rendererRef.current.forceContextLoss();
              }
            } catch (error) {
              console.warn('Error during Buildings3D cleanup:', error);
            }
            
            sceneRef.current = null;
            cameraRef.current = null;
            lightRef.current = null;
            groundRef.current = null;
            buildingsGroupRef.current = null;
            rendererRef.current = null;
          },
        };

        // Only add the layer if it doesn't already exist
        if (!map.getLayer(customLayerId)) {
          map.addLayer(customLayer);
        }

        const onMoveEnd = () => {
          computeOriginAndTransform();
          if (groundRef.current && modelTransformRef.current) {
            groundRef.current.position.y = (modelTransformRef.current as ModelTransform).translateZ;
            groundRef.current.updateMatrixWorld();
          }
          rebuildBuildings();
          applyDirectionalLightFromSun(sunDateRef.current);
          try {
            map.triggerRepaint();
          } catch {}
        };
        const onStyleData = () => { rebuildBuildings(); };
        const onSourceData = (e: MapSourceDataEvent) => {
          if (e.sourceId === sourceId && e.isSourceLoaded) {
            rebuildBuildings();
            map.off('sourcedata', onSourceData as never);
          }
        };
        const onIdleOnce = () => { rebuildBuildings(); };

        map.on('moveend', onMoveEnd);
        map.on('zoomend', onMoveEnd);
        map.on('rotateend', onMoveEnd);
        map.on('pitchend', onMoveEnd);
        map.on('styledata', onStyleData);
        map.on('sourcedata', onSourceData as never);
        map.once('idle', onIdleOnce);

        // Initial rebuild attempt
        setTimeout(() => {
          rebuildBuildings();
        }, 1000);

        return () => {
          map.off('moveend', onMoveEnd);
          map.off('zoomend', onMoveEnd);
          map.off('rotateend', onMoveEnd);
          map.off('pitchend', onMoveEnd);
          map.off('styledata', onStyleData);
          map.off('sourcedata', onSourceData as never);
          cleanupCustomOnly();
        };
      } catch {
        // Error initializing 3D buildings
      }
    };
    
    const tryInitialize = () => {
      if (initializedRef.current) { return; }
      // Only require a style object to exist; do not block on isStyleLoaded()
      // because inline styles may report not fully loaded while still allowing
      // sources/layers to be added safely.
      if (!map.getStyle()) {
        return;
      }
      
      // Ensure source exists before initializing
      if (!ensureSourceAndProbe()) {
        return;
      }
      
      initializedRef.current = true;
      const cleanup = initializeBuildings();
      return cleanup;
    };

    // Wait for the map to be fully ready before initializing
    if (map.isStyleLoaded() && map.getStyle()) {
      initializedRef.current = true;
      const cleanup = initializeBuildings();
      if (cleanup) {
        return cleanup;
      }
    }

    const onLoad = () => { tryInitialize(); };
    const onStyleDataInit = () => { tryInitialize(); };
    const onSourceDataInit = (e: MapSourceDataEvent) => {
      if (e.sourceId === sourceId && e.isSourceLoaded) {
        tryInitialize();
      }
    };
    map.on('load', onLoad);
    map.on('styledata', onStyleDataInit);
    map.on('sourcedata', onSourceDataInit as never);
    
    return () => {
      initializedRef.current = false;
      map.off('load', onLoad);
      map.off('styledata', onStyleDataInit);
      map.off('sourcedata', onSourceDataInit as never);
      cleanupAll();
    };
  }, [map, enabled, highlightAreas, highlightColor, defaultColor]);

  useEffect(() => {
    if (!map || !enabled) { return; }
    applyDirectionalLightFromSun(sunDate);
  }, [map, enabled, sunDate]);

  return null;
};

export default Buildings3D;